import { debounce } from "lodash";
import {
  BoundingBox,
  MotionStyle,
  useMotionValue,
  useTransform,
} from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useCompanyScopeContext,
  useLayoutMainContext,
  useRtl,
} from "@ryde/redux";
import {
  areasGridMapOpenAtom,
  GlobalAppEventEnum,
  Log,
  SPLITTER_MAP_INITIAL_WIDTH,
  SPLITTER_MAP_LEFT_LIMIT,
} from "@ryde/shared";

const MAP_RESIZE_DEBOUNCE_MS = 300;

export const useAreasGridSplitter = (customRightPanelWidth?: number) => {
  const rightPanelWidth = useRef<number>(
    customRightPanelWidth || SPLITTER_MAP_INITIAL_WIDTH,
  );
  const [lastLeftX, setLastLeftX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const { mainElement } = useLayoutMainContext();
  const { eventEmitter } = useCompanyScopeContext();
  const x = useMotionValue(0);
  const { isRtl } = useRtl();
  const previousRtl = useRef(isRtl);
  const mapOpen = useRecoilValue(areasGridMapOpenAtom);

  const mapInvalidateSizeDebounced = useMemo(
    () =>
      debounce(
        () => eventEmitter?.emit(GlobalAppEventEnum.RESIZE_AREAS_MAP),
        MAP_RESIZE_DEBOUNCE_MS,
      ),
    [eventEmitter],
  );

  useEffect(() => {
    const rtlChange = isRtl !== previousRtl.current;
    const computedLeftX = rtlChange ? 0 : lastLeftX;

    if (rtlChange) {
      previousRtl.current = isRtl;

      if (lastLeftX) {
        setLastLeftX(0);
      }
    }

    if ((mapOpen || rtlChange) && mainElement) {
      const initialComputedX =
        computedLeftX ||
        (isRtl
          ? rightPanelWidth.current - mainElement.clientWidth
          : mainElement.clientWidth - rightPanelWidth.current);
      Log.debug("Areas splitter position set to:", initialComputedX);
      x.set(initialComputedX);
    }
  }, [isRtl, lastLeftX, mainElement, mapOpen, x]);

  const onDragStartHandler = useEvent((): void => {
    setDragging(true);
  });

  const onDragEndHandler = useEvent((): void => {
    setDragging(false);
    setLastLeftX(x.get());
    mapInvalidateSizeDebounced();
  });

  useEffect(() => {
    if (!mapOpen) {
      return;
    }

    const unsubscribe = x.on("change", () => {
      mapInvalidateSizeDebounced();
    });

    return () => {
      unsubscribe();
    };
  }, [mapOpen, x, mapInvalidateSizeDebounced]);

  const splitterOffsetStart = useTransform(x, val => {
    return `${Math.abs(val)}px`;
  });

  const gridSizeProps: MotionStyle = useMemo(() => {
    return {
      width: mapOpen ? splitterOffsetStart : "100%",
      insetInlineStart: 0,
      top: 0,
      bottom: 0,
    };
  }, [mapOpen, splitterOffsetStart]);

  const mapSizeProps: MotionStyle = useMemo(() => {
    return { insetInlineStart: splitterOffsetStart };
  }, [splitterOffsetStart]);

  const dragConstraints: Partial<BoundingBox> = useMemo(() => {
    if (isRtl) {
      return { right: SPLITTER_MAP_LEFT_LIMIT };
    }

    return { left: SPLITTER_MAP_LEFT_LIMIT };
  }, [isRtl]);

  return {
    mapOpen,
    x,
    onDragStartHandler,
    onDragEndHandler,
    dragging,
    gridSizeProps,
    mapSizeProps,
    dragConstraints,
  };
};
